import { intl } from "LocaleWrapper";
import { Highlights, PageHeader } from "@cofano/cck";
import Helmet from "components/helmet/Helmet";
import { Language } from "imports/Utils";
import DeclarationViewButtons, { DeclarationView } from "view/declarations/components/buttons/DeclarationViewButtons";
import DeclarationsTable, { DeclarationRow } from "view/declarations/components/table/DeclarationsTable";
import InvoiceLinesTable from "view/invoicing/components/table/InvoiceLinesTable";
import useTabs from "components/hooks/useTabs";
import { Box, Tab, Tabs } from "@mui/material";
import { CharterType, HistoryEntity } from "__generated__/enums";
import DeclareButton from "view/declarations/components/buttons/DeclareButton";
import CalculateIcon from "@mui/icons-material/Calculate";
import HistoryIcon from "@mui/icons-material/History";
import { useEffect, useState } from "react";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";
import { EntityHistoryType } from "components/entityHistory/utils/entityHistory.utils";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";
import { TableId } from "components/table";
import useLayout from "components/layout/useLayout";

enum TabItem {
	Declarations = "declarations",
	InvoiceLines = "invoice-lines"
}

type DeclarationsListProps = {
	charterType: CharterType;
}

const Declarations = ({ charterType }: DeclarationsListProps) => {
	const layout = useLayout();

	const { selectedTab, selectTab } = useTabs<TabItem>({
		defaultSelected: TabItem.Declarations,
	});

	const openCharterSideviewer = useOpenCharterSideviewer();

	const [ snapshotHistory, setSnapshotHistory ] = useState<{ id: number } | null>(null);
	const [ selectedTableRow, selectTableRow ] = useState<DeclarationRow | null>(null);

	const isSale = charterType === CharterType.Sales;
	const titleKey = isSale ? "ADMINISTRATION_SALE_DECLARATIONS" : "PURCHASE_DECLARATIONS";
	const basePath = isSale ? "/view/declarations/sale" : "/view/declarations/purchase";

	useEffect(() => {
		if (!layout.drawerSettings.open && selectedTableRow) {
			selectTableRow(null);
		}
	}, [ layout.drawerSettings.open ]);

	return <div>
		<PageHeader>
			<PageHeader.Title>
				{Language(titleKey)}
				<Helmet title={intl.formatMessage({ id: titleKey })} />
			</PageHeader.Title>

			<PageHeader.Actions>
				<DeclarationViewButtons active={DeclarationView.List} basePath={basePath} />
			</PageHeader.Actions>
		</PageHeader>

		<Box sx={{ borderBottom: 1, borderColor: "divider" }} mb={2}>
			<Tabs
				value={selectedTab}
				onChange={(_event, value) => selectTab(value)}
				indicatorColor="primary">
				<Tab value={TabItem.Declarations} label={intl.formatMessage({ id: "DECLARATIONS" })} />
				<Tab value={TabItem.InvoiceLines} label={intl.formatMessage({ id: "INVOICE_LINES" })} />
			</Tabs>
		</Box>

		{selectedTab === TabItem.Declarations && (
			<DeclarationsTable
				key={`${charterType}-declarations`}
				tableId={charterType === CharterType.Sales ? TableId.SalesDeclarations : TableId.PurchaseDeclarations}
				charterType={charterType}
				highlightRow={row => row.id === selectedTableRow?.id ? Highlights.HIGHLIGHT : Highlights.NONE}
				rowActions={[ {
					icon: <CalculateIcon />,
					onClick: () => undefined,
					hidden: (row) => !row.charter,
					render: (row, refetch) => row.charter ? (
						<DeclareButton
							charter={row.charter}
							invoiceGroupType={row.invoiceGroupType}
							declaration={row}
							refetch={() => refetch()}
						/>
					) : null,
				}, {
					icon: <HistoryIcon />,
					onClick: (row) => setSnapshotHistory(row),
					hidden: (row) => !row.id,
				} ]}
				onRowClick={(row, refetch) => {
					if (row.charter) {
						openCharterSideviewer(row.charter.id, () => refetch(), row.id);
						selectTableRow(row);
					}
				}}
			/>
		)}

		{selectedTab === TabItem.InvoiceLines && (
			<InvoiceLinesTable
				tableId={charterType === CharterType.Sales ? TableId.SalesInvoiceLines : TableId.PurchaseInvoiceLines}
				charterType={charterType}
			/>
		)}

		{snapshotHistory && (
			<EntityHistoryDialog
				open={true}
				onClose={() => setSnapshotHistory(null)}
				entity={HistoryEntity.Declaration}
				entityId={snapshotHistory.id}
				title={intl.formatMessage({ id: "ENTITY_HISTORY" })}
				maxWidth="md"
				type={EntityHistoryType.Absolute}
			/>
		)}
	</div>;
};

export default Declarations;
